import React, { FC, useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import { IComboSlotOption, IItem } from '@rbi-ctg/menu';
import { ProductCtaWrapper } from 'components/product-detail/product-cta';
import { StyledActionButton } from 'components/product-detail/styled';
import { useDialogModal } from 'hooks/use-dialog-modal';
import { useCdpContext } from 'state/cdp';
import { ClickEventComponentNames, CustomEventNames, EventTypes } from 'state/cdp/constants';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useProductWizardContext } from 'state/product-wizard';
import {
  ProductWizardStep,
  UserSelectionModifierMap,
  UserSelectionModifierSlotMap,
} from 'state/product-wizard/types';
import { ProductWizardUtils } from 'state/product-wizard/utils';

import { ProductCustomizationBackButton } from './product-customization-back-button';
import { ProductCustomizationHeader } from './product-customization-header';
import { ProductCustomizationModifiers } from './product-customization-modifiers';
import { ProductCustomizationOptions } from './product-customization-options';
import {
  ProductCustomizationFixedHeader,
  ProductCustomizationPanel,
  ProductCustomizationScrollableArea,
} from './product-customization.styled';
import { IProductCustomizationProps, SelectModifierFn, SelectionError } from './types';
import { getModifiersDefaultOptions } from './utils';

export const ProductCustomization: FC<IProductCustomizationProps> = ({ itemToCustomize }) => {
  const { formatMessage } = useIntl();
  const {
    currentStep,
    goToHome,
    slotKeyToCustomize,
    submitCustomizations,
    userSelections,
    menuObject,
  } = useProductWizardContext();

  // Local state for user's draft changes
  const [selectedItem, setSelectedItem] = useState<IItem>(itemToCustomize);
  const [modifierSelections, setModifierSelections] = useState<UserSelectionModifierSlotMap>(
    userSelections.modifiers[slotKeyToCustomize]
  );

  const [submitted, setSubmitted] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const toggleCollapsed = useCallback(() => setCollapsed(!collapsed), [collapsed]);

  const [defaultModifiersMap] = useState(() => getModifiersDefaultOptions(selectedItem));

  const handleResetModifierSelections = useCallback(
    (item = selectedItem) => {
      const defaultModifiers = ProductWizardUtils.ComputeDefaultModifiersSelections(item);

      setModifierSelections(defaultModifiers);
      submitCustomizations({
        modifiers: defaultModifiers,
        selectedItem,
        slotKey: slotKeyToCustomize,
      });
      setHasUnsavedChanges(false);
    },
    [selectedItem, slotKeyToCustomize, submitCustomizations]
  );

  const handleComboSlotSelectionChange = useCallback(
    (comboSlotOption: IComboSlotOption) => {
      const item = comboSlotOption.option as IItem;
      setSelectedItem(item);
      handleResetModifierSelections(item);
      setHasUnsavedChanges(true);
    },
    [handleResetModifierSelections]
  );

  const handleChangeModifierSelections = useCallback<SelectModifierFn>(
    (selections, modifier) => {
      const mappedSelections = Object.values(selections).reduce((acc, selection) => {
        acc[selection._key] = {
          item: selectedItem,
          itemId: selectedItem._id,
          modifier: selection,
          option: modifier,
          comboSlotId: userSelections?.comboSlot?.get(slotKeyToCustomize)?.comboSlot?._id,
          isSanityDefaultSelection: false,
        };
        return acc;
      }, {} as UserSelectionModifierMap);

      setModifierSelections(current => {
        return {
          ...current,
          [modifier._key]: mappedSelections,
        };
      });
      setHasUnsavedChanges(true);
    },
    [selectedItem, slotKeyToCustomize, userSelections]
  );

  const [MissingSelectionsDialog, openMissingSelectionsDialog] = useDialogModal({
    modalAppearanceEventMessage: 'Menu option selection errors.',
  });
  const missingSelectionsErrors = useMemo(
    () =>
      Object.values(modifierSelections).reduce((errors, modifierMap) => {
        const modifierError = Object.values(modifierMap).reduce(
          (missingError, action) => ({
            name: missingError.name || action.option.name.locale,
            minAmount: missingError.minAmount || action.option.minAmount,
            quantity: missingError.quantity + action.modifier.quantity,
          }),
          { minAmount: 0, quantity: 0, name: '' }
        );
        if (modifierError.minAmount > modifierError.quantity) {
          errors.push(modifierError);
        }
        return errors;
      }, [] as SelectionError[]),
    [modifierSelections]
  );

  const cdp = useCdpContext();
  const handleSubmit = useCallback(() => {
    cdp.trackEvent({
      name: CustomEventNames.CLICK_EVENT,
      type: EventTypes.Navigation,
      attributes: {
        component: ClickEventComponentNames.BUTTON,
        text: 'Update Item',
      },
    });
    setSubmitted(true);
    if (missingSelectionsErrors.length) {
      openMissingSelectionsDialog();
      return;
    }
    submitCustomizations({
      modifiers: modifierSelections,
      selectedItem,
      slotKey: slotKeyToCustomize,
    });
    goToHome();
  }, [
    goToHome,
    cdp,
    missingSelectionsErrors.length,
    modifierSelections,
    openMissingSelectionsDialog,
    selectedItem,
    slotKeyToCustomize,
    submitCustomizations,
  ]);

  const discardUnsavedChanges = useCallback(() => {
    goToHome();
  }, [goToHome]);

  const [ConfirmationDialog, openConfirmationDialog] = useDialogModal({
    onConfirm: handleSubmit,
    onCancel: discardUnsavedChanges,
    onDismiss: discardUnsavedChanges,
    showCancel: true,
    modalAppearanceEventMessage: 'Confirmation: Product Wizard - Discard unsaved changes',
  });

  const handleBackButtonClick = useCallback(() => {
    if (hasUnsavedChanges) {
      openConfirmationDialog();
    } else {
      goToHome();
    }
  }, [hasUnsavedChanges, openConfirmationDialog, goToHome]);

  const comboSlot = useMemo(
    () => userSelections.comboSlot?.get(slotKeyToCustomize)?.comboSlot,
    [slotKeyToCustomize, userSelections.comboSlot]
  );

  const comboSlotOptions = comboSlot?.options || [];
  const hasAvailableOptions = comboSlotOptions.length > 1;
  const isUpsellSimplified = useFlag(LaunchDarklyFlag.ENABLE_PRODUCT_UPSELL_SIMPLIFIED);
  const showExpandedOptions = hasAvailableOptions && (!collapsed || isUpsellSimplified);

  // Prevents rendering two mobile header back buttons
  const showBackButton = currentStep === ProductWizardStep.ProductCustomization;

  const pickerAspectExclusions = useMemo(() => {
    return ProductWizardUtils.GetPickerAspectExclusions(menuObject);
  }, [menuObject]);

  return (
    <ProductCustomizationPanel data-testid="product-customization">
      <ConfirmationDialog
        heading={formatMessage({ id: 'unsavedChangesModalHeader' })}
        body={formatMessage({ id: 'unsavedChangesModalDescription' })}
        confirmLabel={formatMessage({ id: 'save' })}
        cancelLabel={formatMessage({ id: 'discard' })}
      />
      <MissingSelectionsDialog
        heading={formatMessage({ id: 'missingItems' })}
        body={missingSelectionsErrors.map(error => (
          <p key={error.name}>
            {formatMessage(
              { id: 'makeAdditionalSelections' },
              { name: error.name, quantity: error.minAmount - error.quantity }
            )}
          </p>
        ))}
      />
      <ProductCustomizationFixedHeader>
        {showBackButton && (
          <ProductCustomizationBackButton onBackButtonClick={handleBackButtonClick} />
        )}
        <ProductCustomizationHeader
          comboSlot={comboSlot}
          isCollapsed={collapsed}
          isClickable={hasAvailableOptions}
          selectedItem={selectedItem}
          slotKey={slotKeyToCustomize}
          toggleCollapse={toggleCollapsed}
          modifierSelections={modifierSelections}
        />
      </ProductCustomizationFixedHeader>
      <ProductCustomizationScrollableArea>
        {showExpandedOptions && (
          <ProductCustomizationOptions
            comboSlot={comboSlot}
            comboSlotOptions={comboSlotOptions}
            selectedItem={selectedItem}
            onSelectComboSlot={handleComboSlotSelectionChange}
          />
        )}
        <ProductCustomizationModifiers
          modifierSelections={modifierSelections}
          modifierDefaults={defaultModifiersMap}
          onModifierSelectionsChange={handleChangeModifierSelections}
          onResetModifierSelections={handleResetModifierSelections}
          pickerAspectExclusions={pickerAspectExclusions}
          selectedItem={selectedItem}
        />
      </ProductCustomizationScrollableArea>
      <ProductCtaWrapper
        button={
          <StyledActionButton
            disabled={submitted && missingSelectionsErrors.length > 0}
            onClick={handleSubmit}
            fullWidth
            data-testid="customization-submit-button"
          >
            {formatMessage({ id: 'updateItem' })}
          </StyledActionButton>
        }
      />
    </ProductCustomizationPanel>
  );
};
